import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchTenantSchema, saveColumnConfiguration, fetchSensitiveColumns, TableInfo } from '../services/tenantColumnConfigService';
import { getConnectionId, clearAllStorage } from '../utils/storage';
import { notifyParentSessionExpired } from '../utils/embed';
import Toast from '../components/Toast';

interface SelectedConfig {
  [tableName: string]: Set<string>;
}

interface ToastMessage {
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
}

const TableColumnConfig: React.FC = () => {
  const navigate = useNavigate();
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [selectedConfig, setSelectedConfig] = useState<SelectedConfig>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  // UI State
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  const handleLogout = () => {
    clearAllStorage();
    // Embedded in the Rage app: let the parent know the session ended instead
    // of silently dropping the user into the standalone /login flow.
    notifyParentSessionExpired();
    navigate('/login');
  };

  useEffect(() => {
    const savedConnectionId = getConnectionId();
    if (!savedConnectionId) {
      navigate('/database-config');
      return;
    }
    setTenantId(savedConnectionId);
    loadSchema(savedConnectionId);
  }, [navigate]);

  const loadSchema = async (tid: string) => {
    setIsLoading(true);

    try {
      // Fetch schema and sensitive columns in parallel
      const [schemaData, sensitiveColumnsData] = await Promise.all([
        fetchTenantSchema(tid),
        fetchSensitiveColumns(tid)
      ]);

      console.log('Schema Data:', schemaData);
      console.log('Sensitive Columns Data:', sensitiveColumnsData);

      // Validate schema data
      if (!schemaData || !schemaData.schema || !Array.isArray(schemaData.schema)) {
        throw new Error('Invalid schema data received from API');
      }

      setTables(schemaData.schema);

      // Build the selected config based on sensitive columns
      // Sensitive columns should be DESELECTED, all others should be selected
      const defaultConfig: SelectedConfig = {};
      schemaData.schema.forEach(table => {
        const sensitiveColumns = sensitiveColumnsData[table.table_name] || [];
        const allColumns = table.columns.map(col => col.name);

        console.log(`Table: ${table.table_name}`);
        console.log(`  All columns:`, allColumns);
        console.log(`  Sensitive columns:`, sensitiveColumns);

        // Select all columns EXCEPT the sensitive ones
        const selectedColumns = allColumns.filter(col => !sensitiveColumns.includes(col));
        console.log(`  Selected columns:`, selectedColumns);

        defaultConfig[table.table_name] = new Set(selectedColumns);
      });

      console.log('Final Selected Config:', defaultConfig);
      setSelectedConfig(defaultConfig);

      // Show info toast if sensitive columns were loaded
      const totalSensitiveColumns = Object.values(sensitiveColumnsData).reduce(
        (sum, cols) => sum + cols.length,
        0
      );
      if (totalSensitiveColumns > 0) {
        setToast({
          message: `Loaded existing configuration: ${totalSensitiveColumns} sensitive column(s) are deselected`,
          type: 'info'
        });
      }
    } catch (error) {
      console.error('Error loading schema:', error);
      setToast({
        message: error instanceof Error ? error.message : 'Failed to load schema',
        type: 'error'
      });
      setTables([]);
      setSelectedConfig({});
    } finally {
      setIsLoading(false);
    }
  };

  const handleTableToggle = (tableName: string) => {
    setSelectedConfig(prev => {
      const newConfig = { ...prev };
      const table = tables.find(t => t.table_name === tableName);

      if (!table) return prev;

      if (newConfig[tableName] && newConfig[tableName].size > 0) {
        // Deselect all columns
        newConfig[tableName] = new Set();
      } else {
        // Select all columns
        newConfig[tableName] = new Set(table.columns.map(col => col.name));
      }

      return newConfig;
    });
  };

  const handleColumnToggle = (tableName: string, columnName: string) => {
    setSelectedConfig(prev => {
      const newConfig = { ...prev };
      if (!newConfig[tableName]) {
        newConfig[tableName] = new Set();
      }

      const tableSet = new Set(newConfig[tableName]);
      if (tableSet.has(columnName)) {
        tableSet.delete(columnName);
      } else {
        tableSet.add(columnName);
      }

      newConfig[tableName] = tableSet;
      return newConfig;
    });
  };

  const toggleTableExpansion = (tableName: string) => {
    setExpandedTables(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tableName)) {
        newSet.delete(tableName);
      } else {
        newSet.add(tableName);
      }
      return newSet;
    });
  };

  const filteredTables = useMemo(() => {
    if (!tables || tables.length === 0) return [];

    let filtered = tables.filter(table => {
      const matchesSearch = table.table_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        table.columns.some(col => col.name.toLowerCase().includes(searchTerm.toLowerCase()));

      if (showSelectedOnly) {
        return matchesSearch && selectedConfig[table.table_name]?.size > 0;
      }

      return matchesSearch;
    });

    return filtered;
  }, [tables, searchTerm, showSelectedOnly, selectedConfig]);

  const statistics = useMemo(() => {
    if (!tables || tables.length === 0) {
      return { totalTables: 0, totalColumns: 0, selectedTables: 0, selectedColumns: 0 };
    }

    const totalTables = tables.length;
    const totalColumns = tables.reduce((sum, table) => sum + table.columns.length, 0);
    const selectedTables = Object.entries(selectedConfig).filter(([_, cols]) => cols.size > 0).length;
    const selectedColumns = Object.values(selectedConfig).reduce((sum, cols) => sum + cols.size, 0);

    return { totalTables, totalColumns, selectedTables, selectedColumns };
  }, [tables, selectedConfig]);

  const handleSubmit = async () => {
    setIsSaving(true);

    try {
      if (!tenantId) {
        console.error('No connection ID found');
        setToast({ message: 'No connection ID found', type: 'error' });
        setIsSaving(false);
        return;
      }

      // Build payload with DESELECTED columns (inverse of selected)
      const deselectedPayload: { [key: string]: string[] } = {};

      tables.forEach(table => {
        const selectedColumns = selectedConfig[table.table_name] || new Set();
        const deselectedColumns = table.columns
          .filter(col => !selectedColumns.has(col.name))
          .map(col => col.name);

        if (deselectedColumns.length > 0) {
          deselectedPayload[table.table_name] = deselectedColumns;
        }
      });

      // Check if there are any sensitive columns to save
      const hasDeselectedColumns = Object.keys(deselectedPayload).length > 0;
      console.log('Deselected Payload:', deselectedPayload);
      if (!hasDeselectedColumns) {
        // No sensitive columns selected - show info message
        setToast({
          message: 'No sensitive columns to save. All columns are included in queries.',
          type: 'info'
        });
      }

      // Always call the API to advance onboarding to collection creation.
      await saveColumnConfiguration(tenantId, deselectedPayload);
      setToast({ message: 'Configuration saved successfully!', type: 'success' });
      setIsSaving(false);

      // Collections are now required before entering the dashboard.
      setTimeout(() => {
        navigate('/create-collection');
      }, 500);

    } catch (error) {
      console.error('Error saving configuration:', error);
      setToast({
        message: error instanceof Error ? error.message : 'Failed to save configuration',
        type: 'error'
      });
      setIsSaving(false);
    }
  };

  const expandAll = () => {
    setExpandedTables(new Set(filteredTables.map(t => t.table_name)));
  };

  const collapseAll = () => {
    setExpandedTables(new Set());
  };

  const selectAll = () => {
    if (!tables || tables.length === 0) return;

    const newConfig: SelectedConfig = {};
    tables.forEach(table => {
      newConfig[table.table_name] = new Set(table.columns.map(col => col.name));
    });
    setSelectedConfig(newConfig);
  };

  const deselectAll = () => {
    if (!tables || tables.length === 0) return;

    const newConfig: SelectedConfig = {};
    tables.forEach(table => {
      newConfig[table.table_name] = new Set();
    });
    setSelectedConfig(newConfig);
  };

  if (tenantId === null || isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl mb-6 shadow-lg">
            <svg className="animate-spin h-10 w-10 text-white" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <p className="text-gray-700 dark:text-gray-300 text-lg font-medium">Loading schema...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Full Screen Loading Overlay */}
      {isSaving && (
        <div className="fixed inset-0 bg-white/60 dark:bg-gray-900/60 backdrop-blur-sm z-[100] flex items-center justify-center">
          <div className="flex flex-col items-center p-8 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-100 border-t-blue-600 mb-4"></div>
            <h2 className="text-xl font-bold text-gray-800 dark:text-white">Saving Configuration</h2>
            <p className="text-gray-500 dark:text-gray-400 mt-2">Please wait while we update your table settings...</p>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-lg sticky top-0 z-50 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-4">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-lg">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Table & Column Configuration
                </h1>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  Select tables and columns to include in your AI-powered queries
                </p>
              </div>
            </div>
            {/* <button
              type="button"
              onClick={handleLogout}
              className="inline-flex items-center px-5 py-2.5 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-800 rounded-lg shadow-sm hover:bg-red-100 dark:hover:bg-red-900/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button> */}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-5 border-l-4 border-blue-500 hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Tables</div>
                <div className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{statistics.totalTables}</div>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-5 border-l-4 border-green-500 hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Columns</div>
                <div className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{statistics.totalColumns}</div>
              </div>
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-5 border-l-4 border-purple-500 hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Selected Tables</div>
                <div className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{statistics.selectedTables}</div>
              </div>
              <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <svg className="w-8 h-8 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-5 border-l-4 border-pink-500 hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Selected Columns</div>
                <div className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{statistics.selectedColumns}</div>
              </div>
              <div className="p-3 bg-pink-100 dark:bg-pink-900/30 rounded-lg">
                <svg className="w-8 h-8 text-pink-600 dark:text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Controls Bar */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-5 mb-6 border border-gray-200 dark:border-gray-700">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="flex-1 w-full lg:w-auto">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search tables or columns..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  disabled={isSaving}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <svg className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowSelectedOnly(!showSelectedOnly)}
                disabled={isSaving}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${showSelectedOnly
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                  }`}
              >
                {showSelectedOnly ? 'Show All' : 'Selected Only'}
              </button>

              <div className="flex gap-2 border-l pl-2 border-gray-300 dark:border-gray-600">
                <button
                  onClick={expandAll}
                  disabled={isSaving}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Expand All
                </button>
                <button
                  onClick={collapseAll}
                  disabled={isSaving}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Collapse All
                </button>
              </div>

              <div className="flex gap-2 border-l pl-2 border-gray-300 dark:border-gray-600">
                <button
                  onClick={selectAll}
                  disabled={isSaving}
                  className="px-3 py-2 text-sm font-medium text-green-700 bg-green-100 rounded-lg hover:bg-green-200 dark:bg-green-900 dark:text-green-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Select All
                </button>
                <button
                  onClick={deselectAll}
                  disabled={isSaving}
                  className="px-3 py-2 text-sm font-medium text-red-700 bg-red-100 rounded-lg hover:bg-red-200 dark:bg-red-900 dark:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Deselect All
                </button>
              </div>

              <div className="flex gap-2 border-l pl-2 border-gray-300 dark:border-gray-600">
                <button
                  onClick={() => setViewMode('grid')}
                  disabled={isSaving}
                  className={`p-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'}`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  disabled={isSaving}
                  className={`p-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'}`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tables Grid/List */}
        <div className={viewMode === 'grid' ? 'grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6' : 'space-y-4 mb-6'}>
          {filteredTables.length === 0 ? (
            <div className="col-span-2 text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="mt-4 text-gray-600 dark:text-gray-400">No tables found matching your search</p>
            </div>
          ) : (
            filteredTables.map((table) => {
              const isExpanded = expandedTables.has(table.table_name);
              const tableSelected = selectedConfig[table.table_name]?.size || 0;
              const totalColumns = table.columns.length;
              const isFullySelected = tableSelected === totalColumns;
              const isPartiallySelected = tableSelected > 0 && tableSelected < totalColumns;

              return (
                <div
                  key={table.table_name}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-200 overflow-hidden border border-gray-200 dark:border-gray-700"
                >
                  {/* Table Header */}
                  <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-700 dark:to-gray-750 border-b border-gray-200 dark:border-gray-600">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 flex-1">
                        <input
                          type="checkbox"
                          checked={isFullySelected}
                          ref={(el) => {
                            if (el) el.indeterminate = isPartiallySelected;
                          }}
                          onChange={() => handleTableToggle(table.table_name)}
                          disabled={isSaving}
                          className="h-5 w-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                            {/* <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg> */}
                            {table.table_name}
                          </h3>
                          <div className="flex items-center space-x-4 mt-1">
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              {tableSelected} / {totalColumns} columns selected
                            </span>
                            {/* {tableSelected > 0 && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                Active
                              </span>
                            )} */}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => toggleTableExpansion(table.table_name)}
                        className="ml-4 p-2 hover:bg-white dark:hover:bg-gray-600 rounded-lg transition-colors"
                      >
                        <svg
                          className={`w-5 h-5 text-gray-600 dark:text-gray-300 transform transition-transform ${isExpanded ? 'rotate-180' : ''
                            }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Columns List */}
                  {isExpanded && (
                    <div className="p-4 pt-16 max-h-96 overflow-y-auto">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {table.columns.map((column) => {
                          const isSelected = selectedConfig[table.table_name]?.has(column.name);

                          return (
                            <div key={column.name} className="group">
                              <label
                                className={`flex items-start p-3 rounded-lg cursor-pointer transition-all relative ${isSelected
                                  ? 'bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-300 dark:border-blue-700'
                                  : 'bg-gray-50 dark:bg-gray-700/50 border-2 border-transparent hover:border-gray-300 dark:hover:border-gray-600'
                                  }`}
                                title={`${column.name} - ${column.data_type}${!column.is_nullable ? ' (NOT NULL)' : ''}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleColumnToggle(table.table_name, column.name)}
                                  disabled={isSaving}
                                  className="mt-1 h-4 w-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                />
                                <div className="ml-3 flex-1 min-w-0">
                                  <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                    {column.name}
                                  </div>
                                </div>

                                {/* Enhanced Tooltip - Right side */}
                                <div className="absolute top-full top-0 ml-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[9999] hidden group-hover:block">
                                  <div className="bg-gray-900 dark:bg-gray-950 text-white text-xs rounded-lg shadow-2xl px-4 py-3 border border-gray-700 whitespace-nowrap">
                                    <div className="font-bold mb-2 text-sm">{column.name}</div>
                                    {column.column_default && (
                                      <div className="mt-2 text-gray-300 text-xs">
                                        <span className="font-semibold">Default:</span> {column.column_default}
                                      </div>
                                    )}
                                    {/* Tooltip Arrow - Left pointing */}
                                  </div>
                                </div>
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer Actions */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 sticky bottom-0 border border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <button
              onClick={() => navigate('/llm-instructions')}
              disabled={isSaving}
              className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 rounded-lg shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Instructions
            </button>

            <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 px-4 py-2 rounded-lg">
                <span className="text-blue-600 dark:text-blue-400">{statistics.selectedTables}</span> tables,
                <span className="text-purple-600 dark:text-purple-400 ml-1">{statistics.selectedColumns}</span> columns selected
              </div>
              <button
                onClick={handleSubmit}
                disabled={isSaving || statistics.selectedColumns === 0}
                className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 border border-transparent rounded-lg hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105 active:scale-95 shadow-lg"
              >
                {isSaving ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving Configuration...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Save Configuration
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableColumnConfig;
